import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, Printer, Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import BudgetSummaryContent from "@/budget/components/BudgetSummaryContent";

const API = `${window.location.origin}/api/budget`;

export default function BudgetSummary() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultQualification, setDefaultQualification] = useState("");
  // Follows the Budget Builder's "Prices Shown/Hidden" toggle (default hidden).
  const showPrices = (() => {
    try { return localStorage.getItem("abs_bb_show_prices") === "1"; } catch { return false; }
  })();

  useEffect(() => {
    axios.get(`${API}/budgets/${id}`)
      .then(r => setBudget(r.data))
      .catch(() => toast.error("Budget not found"))
      .finally(() => setLoading(false));
    axios.get(`${API}/settings`)
      .then(r => setDefaultQualification(r.data.default_qualification_text || ""))
      .catch(() => {});
  }, [id]);

  if (loading) return <div className="p-8 text-slate-400">Loading...</div>;
  if (!budget) return <div className="p-8 text-slate-500">Budget not found.</div>;

  return (
    <div className="min-h-full bg-[#F8FAFC]" data-testid="budget-summary">
      {/* Toolbar - no-print */}
      <div className="no-print flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center gap-3">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-slate-500" data-testid="breadcrumbs">
            <button onClick={() => navigate("/dashboard")} className="hover:text-[#007BFF] hover:underline" data-testid="breadcrumb-budgets">Budgets</button>
            <ChevronRight className="h-3 w-3 opacity-60" />
            <button onClick={() => navigate(`/budgets/${id}`)} className="hover:text-[#007BFF] hover:underline" data-testid="back-to-editor-btn">Budget</button>
            <ChevronRight className="h-3 w-3 opacity-60" />
            <span className="font-medium text-slate-700">Summary</span>
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()} className="rounded-sm border-slate-300 text-slate-600 gap-1.5 text-xs" data-testid="print-btn">
            <Printer className="w-3.5 h-3.5" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="rounded-sm border-slate-300 text-slate-600 gap-1.5 text-xs" data-testid="export-pdf-btn">
            <Download className="w-3.5 h-3.5" /> PDF Export
          </Button>
          <Button variant="outline" size="sm" disabled className="rounded-sm border-slate-300 text-slate-400 gap-1.5 text-xs" title="Coming soon" data-testid="export-excel-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel Export
          </Button>
        </div>
      </div>
      <BudgetSummaryContent budget={budget} defaultQualification={defaultQualification} showPrices={showPrices} />

      {/* Print-only clone — portal-mounted on <body> so it renders outside the
          hidden #root during printing (see index.css `@media print`). */}
      {createPortal(
        <div className="print-only" aria-hidden="true">
          <BudgetSummaryContent budget={budget} defaultQualification={defaultQualification} showPrices={showPrices} />
        </div>,
        document.body,
      )}
    </div>
  );
}
